import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';

import { actions } from 'features/users/redux';
// import { actions as treeActions } from 'features/tree';

import { balanceSortTypes, usernameSortTypes } from '../data';

const useUsersModel = () => {
  const dispatch = useDispatch();

  const { tab } = useParams();

  const { id } = useSelector(state => state.auth, shallowEqual);
  const { isNeedToRefresh } = useSelector(state => state.users, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { selectedAgentId, agentClickedTrigger } = useSelector(state => state.tree, shallowEqual);

  const firstRender = useRef(true);

  const [filterValue, setFilterValue] = useState('');
  const [count, setCount] = useState(10);
  const [actionProcessing, changeActionProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [itemsOnPage, setItemsOnPage] = useState({ value: 10, name: '10' });
  const [date, setDate] = useState({
    beginDate: null,
    endDate: null,
  });
  const [isDirectStructure, setDirectStructure] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [isLoadingSearchResult, setIsLoadingSearchResult] = useState(false);

  const [balanceSortType, setBalanceSortType] = useState({
    ...balanceSortTypes[2],
    name: `(${locale[balanceSortTypes[2].name]})`,
  });

  const [usernameSortType, setUsernameSortType] = useState({
    ...usernameSortTypes[2],
    name: `(${locale[usernameSortTypes[2].name]})`,
  });

  const role = useMemo(() => {
    if (tab === 'players') {
      setPage(0);
      return 0;
    }
    if (tab === 'agents') {
      setPage(0);
      return 6;
    }
    if (tab === 'all' || tab === 'disabled') {
      setPage(0);
      return '';
    }
  }, [tab]);

  const isBanned = useMemo(() => {
    if (tab === 'disabled') {
      return true;
    } else return false;
  }, [tab]);

  const debouncedSearchUser = useRef(
    debounce(async value => {
      if (value.length >= 4) {
        setIsLoadingSearchResult(true);
        dispatch(
          actions.getUsersListForSearch({
            username: value,
            callback: data => {
              if (data) setSearchResult(data.map(i => i.username));
              setIsLoadingSearchResult(false);
            },
          }),
        );
      }
    }, 800),
  );

  const handleSearchChange = e => {
    const { value } = e.target;
    setFilterValue(value);

    if (value.length >= 4) {
      setIsLoadingSearchResult(true);
      debouncedSearchUser.current(value, isDirectStructure);
    } else {
      setIsLoadingSearchResult(false);
      setSearchResult([]);
    }
  };

  const handleResultClick = username => {
    setFilterValue(username);
    setIsLoadingSearchResult(false);
    setSearchResult([]);

    dispatch(
      actions.getUsersList({
        username,
        role,
        userId: selectedAgentId ?? id,
        isBanned,
        count,
        page: page || 0,
        // order_by: balanceSortType.value !== 0 ? balanceSortType.value : usernameSortType.value,
        date_from: date.beginDate ? dayjs(date.beginDate).format('YYYY-MM-DD') : '',
        date_to: date.endDate ? dayjs(date.endDate).format('YYYY-MM-DD') : '',
        isDirectStructure,
      }),
    );
  };

  const selectItemChangedHandler = itemValue => {
    setItemsOnPage({ value: itemValue, name: itemValue });
    onItemsOnPageChanged(itemValue);
  };

  const onSetBalanceSort = type => {
    const match = balanceSortTypes.find(el => el.value === type);
    setBalanceSortType({ ...match, name: `(${locale[match.name]})` });

    const match2 = usernameSortTypes[2];
    setUsernameSortType({ ...match2, name: `(${locale[match2.name]})` });
  };

  const onSetUsernameSort = type => {
    const match = usernameSortTypes.find(el => el.value === type);
    setUsernameSortType({ ...match, name: `(${locale[match.name]})` });

    const match2 = balanceSortTypes[2];
    setBalanceSortType({ ...match2, name: `(${locale[match2.name]})` });
  };

  const onChangeFilterValue = e => {
    const { value } = e.currentTarget;
    setFilterValue(value);
    debouncedSearchUser.current(value);
  };

  const onItemsOnPageChanged = newItemsOnPage => {
    setCount(newItemsOnPage);
    setPage(0);
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, beginDate: value });
    }
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, endDate: value });
    }
  };

  const onResetFilter = () => {
    setFilterValue('');
    setDate({
      beginDate: null,
      endDate: null,
    });
    getUserWithFilter({
      username: '',
      date_from: '',
      date_to: '',
    });
  };

  const onChangeDirectStructure = value => {
    setDirectStructure(value);
  };

  const getUserWithFilter = () => {
    const orderBy =
      balanceSortType.value === 0 && usernameSortType.value === 0
        ? null
        : balanceSortType.value !== 0
        ? balanceSortType.value
        : usernameSortType.value;

    dispatch(
      actions.getUsersList({
        username: filterValue,
        role,
        userId: selectedAgentId ?? id,
        count,
        page,
        order_by: orderBy,
        date_from: date.beginDate ? dayjs(date.beginDate).format('YYYY-MM-DD') : '',
        date_to: date.endDate ? dayjs(date.endDate).format('YYYY-MM-DD') : '',
        isDirectStructure,
        isBanned,
      }),
    );
  };

  const getTreeByUsername = () => {
    changeActionProcessing(true);
    getUserWithFilter(
      {
        // userId: !filterValue ? selectedAgentId ?? id : undefined,
        userId: selectedAgentId ?? id,
      },
      true,
    );
    // .then(user => {
    //   if (user?.id) {
    //     dispatch(
    //       treeActions.getUsersByUserId(
    //         { id: user.id, nesting: [user.id], name: user.username },
    //         true
    //       )
    //     )
    //     .finally(() => {
    //       dispatch(treeActions.selectAgentId(user.id));
    //       if (filterValue) {
    //         setFilterValue('');
    //         getUserWithFilter({ username: '', userId: user.id });
    //       }

    //       changeActionProcessing(false);
    //     });
    //   } else {
    //     changeActionProcessing(false);
    //   }
    // });
    changeActionProcessing(false);
    setSearchResult([]);
  };

  useEffect(() => {
    if (filterValue.length < 4) setSearchResult([]);
  }, [filterValue, JSON.stringify(searchResult)]);

  useEffect(() => {
    if (!firstRender.current) {
      onResetFilter();
    }

    firstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentClickedTrigger]);

  useEffect(() => {
    getTreeByUsername();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isBanned, id, page, itemsOnPage.value, isNeedToRefresh, balanceSortType, usernameSortType]);

  // useEffect(() => {
  //   if (filterValue.length >= 4) {
  //     getUserWithFilter();
  //   }
  // }, [filterValue, page, itemsOnPage.value, balanceSortType, usernameSortType, isNeedToRefresh, isDirectStructure]);

  return {
    count,
    filterValue,
    page,
    date,
    itemsOnPage,
    balanceSortType,
    usernameSortType,
    role,
    actionProcessing,
    isBanned,
    onChangeFilterValue,
    onChangeBeginDate,
    onChangeEndDate,
    onResetFilter,
    onSetBalanceSort,
    onSetUsernameSort,
    onItemsOnPageChanged,
    setPage,
    setBalanceSortType,
    setUsernameSortType,
    selectItemChangedHandler,
    getTreeByUsername,
    getUserWithFilter,
    onChangeDirectStructure,
    isDirectStructure,
    searchResult,
    handleSearchChange,
    handleResultClick,
    isLoadingSearchResult,
  };
};

export default useUsersModel;
